import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Upload as UploadIcon, FileSpreadsheet, CheckCircle, X, ClipboardCheck, Loader2 } from 'lucide-react';

// ── Single-file dropzone (for jobs) ─────────────────────────────────────────
function DropZone({ label, hint, accept, file, onFile }) {
  const onDrop = useCallback(accepted => { if (accepted[0]) onFile(accepted[0]); }, [onFile]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept, multiple: false });

  return (
    <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors
      ${isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-gray-50'}`}>
      <input {...getInputProps()} />
      <UploadIcon className="mx-auto text-gray-300 mb-3" size={36} />
      {file ? (
        <div className="flex items-center justify-center gap-2 text-green-600 font-medium">
          <FileSpreadsheet size={18} />
          <span>{file.name}</span>
          <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
        </div>
      ) : (
        <div>
          <p className="text-gray-600 font-medium">{label}</p>
          <p className="text-sm text-gray-400 mt-1">{hint}</p>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Upload() {
  const [jobsFile,     setJobsFile]     = useState(null);
  const [jobsResult,   setJobsResult]   = useState(null);
  const [jobsLoading,  setJobsLoading]  = useState(false);
  const [batches,      setBatches]      = useState([]);

  const [auditFile,    setAuditFile]    = useState(null);
  const [auditResult,  setAuditResult]  = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    api.get('/uploads/batches').then(r => setBatches(r.data)).catch(() => {});
  }, []);

  const EXCEL_ACCEPT = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
    'application/vnd.ms-excel': ['.xls'],
    'text/csv': ['.csv'],
  };

  // ── Jobs upload (single file) ──────────────────────────────────────────────
  async function handleJobsUpload() {
    if (!jobsFile) return;
    setJobsLoading(true);
    const form = new FormData();
    form.append('file', jobsFile);
    try {
      const r = await api.post('/uploads/', form);
      setJobsResult(r.data);
      toast.success(`Imported ${r.data.inserted} jobs`);
      const br = await api.get('/uploads/batches');
      setBatches(br.data);
      setJobsFile(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setJobsLoading(false); }
  }

  // ── Audit upload (single file) ─────────────────────────────────────────────
  async function handleAuditUpload() {
    if (!auditFile) return;
    setAuditLoading(true);
    const form = new FormData();
    form.append('file', auditFile);
    try {
      const r = await api.post('/uploads/audit-results', form);
      setAuditResult(r.data);
      toast.success(`Imported ${r.data.inserted} new + ${r.data.updated} updated`);
      const br = await api.get('/uploads/batches');
      setBatches(br.data);
      setAuditFile(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setAuditLoading(false); }
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-10">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Upload</h1>
        <p className="text-sm text-gray-500 mt-1">Import jobs for auditing or upload completed audit results</p>
      </div>

      {/* ── Section 1: Jobs File ──────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#3D2B1A] flex items-center justify-center text-white text-sm font-bold">1</div>
          <div>
            <h2 className="text-base font-semibold text-gray-800">Jobs File — ServiceTitan Export</h2>
            <p className="text-xs text-gray-400">
              Upload your <code className="bg-gray-100 px-1 rounded">PCC Audit_Dated ___</code> file.
              One job per technician will be queued for auditing. Duplicates are skipped automatically.
            </p>
          </div>
        </div>

        <DropZone
          label="Drop ServiceTitan export here"
          hint="PCC Audit_Dated ___ · .xlsx / .xlsm / .xls / .csv"
          accept={EXCEL_ACCEPT}
          file={jobsFile}
          onFile={setJobsFile}
        />

        {jobsFile && (
          <div className="flex gap-3">
            <button onClick={handleJobsUpload} disabled={jobsLoading}
              className="flex-1 py-2.5 bg-[#3D2B1A] hover:bg-[#E8761A] text-white font-medium rounded-lg disabled:opacity-40 transition-colors">
              {jobsLoading ? 'Importing...' : 'Import Jobs'}
            </button>
            <button onClick={() => setJobsFile(null)}
              className="px-4 py-2.5 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50">
              <X size={18} />
            </button>
          </div>
        )}

        {jobsResult && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-5">
            <div className="flex items-center gap-2 text-green-700 font-semibold mb-3">
              <CheckCircle size={18} /> Import Complete
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm text-center">
              <div>
                <div className="text-2xl font-bold text-green-700">{jobsResult.inserted}</div>
                <div className="text-green-600 text-xs">Jobs Queued</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-600">{jobsResult.skipped_duplicates}</div>
                <div className="text-amber-600 text-xs">Duplicates Skipped</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-600">{jobsResult.total_rows}</div>
                <div className="text-gray-500 text-xs">Total Rows in File</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <hr className="border-gray-100" />

      {/* ── Section 2: Audit Results (single file) ────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#E8761A] flex items-center justify-center text-white text-sm font-bold">2</div>
          <div>
            <h2 className="text-base font-semibold text-gray-800">Audit Results — PCC Audit Tool</h2>
            <p className="text-xs text-gray-400">
              Drop a completed <code className="bg-gray-100 px-1 rounded">PCC_AUDIT_TOOL ___.xlsm</code> file.
              All audited rows from the <strong>Audit View</strong> sheet will be imported.
            </p>
          </div>
        </div>

        <DropZone
          label="Drop completed Audit Tool file here"
          hint="PCC_AUDIT_TOOL ___ · .xlsm / .xlsx"
          accept={EXCEL_ACCEPT}
          file={auditFile}
          onFile={setAuditFile}
        />

        {auditFile && (
          <div className="flex gap-3">
            <button onClick={handleAuditUpload} disabled={auditLoading}
              className="flex-1 py-2.5 bg-[#E8761A] hover:bg-[#3D2B1A] text-white font-medium rounded-lg disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
              {auditLoading
                ? <><Loader2 size={16} className="animate-spin" /> Importing…</>
                : 'Import Audit Results'}
            </button>
            <button onClick={() => setAuditFile(null)}
              className="px-4 py-2.5 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50">
              <X size={18} />
            </button>
          </div>
        )}

        {auditResult && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <div className="flex items-center gap-2 text-blue-700 font-semibold mb-3">
              <ClipboardCheck size={18} /> Audit Results Imported
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm text-center">
              <div>
                <div className="text-2xl font-bold text-green-700">{auditResult.inserted}</div>
                <div className="text-green-600 text-xs">New Results</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-600">{auditResult.updated}</div>
                <div className="text-amber-600 text-xs">Updated</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-600">{auditResult.total}</div>
                <div className="text-gray-500 text-xs">Total Records</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Import History ────────────────────────────────────────────────── */}
      {batches.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-700">Import History</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="px-4 py-2 text-left text-xs font-medium">Filename</th>
                <th className="px-4 py-2 text-left text-xs font-medium">Jobs</th>
                <th className="px-4 py-2 text-left text-xs font-medium">Imported By</th>
                <th className="px-4 py-2 text-left text-xs font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {batches.map(b => (
                <tr key={b.id} className="border-t border-gray-50">
                  <td className="px-4 py-2 font-mono text-xs text-gray-600">{b.filename}</td>
                  <td className="px-4 py-2 text-gray-600">{b.job_count}</td>
                  <td className="px-4 py-2 text-gray-600">{b.imported_by_name || '—'}</td>
                  <td className="px-4 py-2 text-gray-400 text-xs">{new Date(b.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
